import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useModal } from 'react-modal-hook';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { RequisiteSelectModal } from 'Components/RequisiteComponents';
import { useDropdown } from 'Hooks/Common';
import { useDocumentFieldColor } from 'Hooks/DocumentFields';
import interact from 'interactjs';
import { SelectableOption } from 'Interfaces/Common';
import { InteractFieldModalPosition, InteractModes, Signer } from 'Interfaces/Document';
import {
  DocumentField,
  DocumentFieldsCRUDMeta,
  DocumentFieldTypes,
  DocumentFieldUpdatePayload,
} from 'Interfaces/DocumentFields';
import {
  defaultRequisiteTypesOrder,
  Requisite,
  RequisiteType,
  SelectedSignature,
} from 'Interfaces/Requisite';
import { flatten } from 'lodash-es';
import Interact from 'Services/Interact';
import { checkIfDateOrText, isCheckbox, isRequisite } from 'Utils/functions';
import { selectDocumentSigner } from 'Utils/selectors';

import { fieldHasValue, getMinSize } from '../../common/utils';
import FieldItemView from '../FieldItemView';

import FieldItemMenu from './FieldItemMenu';
import FieldTrigger from './FieldTrigger';

export interface FieldItemProps {
  field: DocumentField;
  inFocus?: boolean;
  signersOptions?: SelectableOption<string>[];
  onChangeSigner: (signerId: string) => void;
  documentScale: number;
  onDocumentFieldUpdate?: (
    documentField: DocumentFieldUpdatePayload,
    meta?: DocumentFieldsCRUDMeta,
  ) => void;
  onFocus?: (id: string) => void;
  onBlur?: (id: string) => void;
  interactMode?: InteractModes;
  disabled?: boolean;
  isResizable?: boolean;
  onInteract?: (fieldId: DocumentField['id']) => void;
  lastSelectedSignature?: SelectedSignature | null;
  setLastSelectedSignature?: (signature: SelectedSignature) => void;
  isFontResize?: boolean;
  popupAvailable?: boolean;
  selectedScale?: number;
  lastFocusedFieldId?: string | null;
  setLastFocusedFieldId?: (fieldId: string) => void;
}

function FieldItem({
  field,
  inFocus,
  onFocus,
  onBlur,
  signersOptions = [],
  onChangeSigner,
  documentScale,
  interactMode = InteractModes.PREPARING,
  disabled,
  onInteract,
  isResizable = true,
  onDocumentFieldUpdate,
  lastSelectedSignature,
  setLastSelectedSignature,
  isFontResize = true,
  selectedScale = 1,
  popupAvailable = true,
  lastFocusedFieldId,
  setLastFocusedFieldId,
}: FieldItemProps) {
  const {
    id,
    type,
    checked,
    signerId,
    required,
    style,
    pageNumber,
    requisiteId,
    minimizeWidth,
    documentId,
    availableSignatureTypes,
  } = field;

  const isCheckboxType = isCheckbox(type);
  const isRequisiteType = isRequisite(type);

  const [containerRef, isMenuOpen, openMenu] = useDropdown();
  const textFieldRef = useRef<HTMLTextAreaElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dropdownMenuClassName, setDropdownMenuClassName] = useState(
    'fieldDropDown__content-wrapper--bottom',
  );

  const [fieldModalPosition, setFieldModalPosition] = useState<
    InteractFieldModalPosition
  >(InteractFieldModalPosition.BOTTOM);

  const getGeneralArrayOfPagesComponents = useCallback(() => {
    const fileNodes = containerRef.current?.parentNode?.firstChild?.childNodes;

    if (fileNodes) {
      const pageArray: ChildNode[] = [];

      fileNodes.forEach(child => {
        pageArray.push(...flatten(child.childNodes));
      });

      return pageArray;
    }
  }, [containerRef]);

  const getRelatedPage = useCallback((): HTMLElement => {
    const pages = getGeneralArrayOfPagesComponents() as HTMLElement[];

    return pages[pageNumber - 1];
  }, [getGeneralArrayOfPagesComponents, pageNumber]);

  const updateDocumentField = useCallback(
    (payload: Omit<DocumentFieldUpdatePayload, 'id'>, meta?: DocumentFieldsCRUDMeta) => {
      onDocumentFieldUpdate?.({ id, ...payload, type }, meta);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, onDocumentFieldUpdate],
  );

  const signer = useSelector(state =>
    selectDocumentSigner(state, { documentId, signerId }),
  ) as Signer;

  const isPreparer = useMemo(() => !!signer?.isPreparer, [signer]);

  const fieldColor = useDocumentFieldColor(signer, interactMode);

  const handleFocus = useCallback(() => {
    onFocus?.(id);
  }, [id, onFocus]);

  const handleBlur = useCallback(() => {
    onBlur?.(id);
  }, [id, onBlur]);

  const { viewStyles, textStyles } = useMemo(() => {
    if (!style) return { viewStyles: {}, textStyles: {} };

    const { fontSize, fontFamily, ...viewStyles } = style;

    return {
      viewStyles,
      textStyles: {
        fontSize: fontSize || 8,
        fontFamily,
      },
    };
  }, [style]);

  const handleRequisiteAssign = useCallback(
    (requisite: Requisite) => {
      if (setLastSelectedSignature) {
        setLastSelectedSignature({
          id: type === DocumentFieldTypes.Signature ? requisite.id : requisite.siblingId,
          siblingId:
            type === DocumentFieldTypes.Initials ? requisite.id : requisite.siblingId,
        });
      }

      updateDocumentField({
        requisiteId: requisite.id,
      });
    },
    [type, setLastSelectedSignature, updateDocumentField],
  );

  const [openRequisiteSelectModal, closeRequisiteSelectModal] = useModal(
    () => (
      <RequisiteSelectModal
        signer={signer}
        onClose={closeRequisiteSelectModal}
        type={
          type === DocumentFieldTypes.Signature
            ? RequisiteType.SIGN
            : RequisiteType.INITIAL
        }
        availableSignatureTypes={availableSignatureTypes || defaultRequisiteTypesOrder}
        onSelect={(requisite: Requisite) => {
          handleRequisiteAssign(requisite);
          closeRequisiteSelectModal();
        }}
      />
    ),
    [handleRequisiteAssign, requisiteId],
  );

  const modifyDropDownMenu = useCallback(() => {
    if (dropdownMenuRef.current && containerRef.current) {
      const containerRect = interact.getElementRect(
        containerRef.current.parentNode as HTMLElement,
      );
      const triggerRect = interact.getElementRect(containerRef.current);
      const dropdownMenuRect = interact.getElementRect(dropdownMenuRef.current);
      const escapeRight =
        containerRect.right < triggerRect.left + dropdownMenuRect.width * selectedScale;
      const escapeBottom =
        containerRect.bottom <
        triggerRect.bottom + dropdownMenuRect.height * selectedScale + 20 * documentScale;

      const isOverflowX =
        containerRect.bottom < dropdownMenuRect.height * (1 / selectedScale);
      const isOverflowY =
        containerRect.right < dropdownMenuRect.width * (1 / selectedScale);

      const isOverflow = isOverflowX || isOverflowY;

      setFieldModalPosition(
        (!escapeRight && !escapeBottom) || isOverflow
          ? InteractFieldModalPosition.BOTTOM
          : escapeRight && !escapeBottom
          ? InteractFieldModalPosition.LEFT
          : escapeBottom && !escapeRight
          ? InteractFieldModalPosition.TOP
          : InteractFieldModalPosition.TOP_LEFT,
      );

      setDropdownMenuClassName(
        classNames({
          'fieldDropDown__content-wrapper--bottom':
            (!escapeRight && !escapeBottom) || isOverflow,
          'fieldDropDown__content-wrapper--left':
            escapeRight && !escapeBottom && !isOverflow,
          'fieldDropDown__content-wrapper--top':
            escapeBottom && !escapeRight && !isOverflow,
          'fieldDropDown__content-wrapper--top-left':
            escapeBottom && escapeRight && !isOverflow,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMenuOpen, dropdownMenuRef, containerRef, documentScale]);

  useLayoutEffect(modifyDropDownMenu, [modifyDropDownMenu]);

  const handleInteraction = useCallback(() => {
    onInteract?.(id);
  }, [id, onInteract]);

  const toggleFieldChecked = useCallback(() => {
    updateDocumentField({ checked: !checked });
  }, [checked, updateDocumentField]);

  const imgRef = useRef<HTMLImageElement>(null);

  const setPrepareInteracting = useCallback(() => {
    const fieldContainer = containerRef.current as HTMLElement;
    const relatedPage = getRelatedPage();
    const minimumSize = getMinSize(type);
    const interactable = new Interact(fieldContainer, relatedPage, relatedPage);

    if (isCheckboxType) {
      minimumSize.width = minimumSize.height = 10;
    }

    minimumSize.width = minimumSize.width * documentScale;
    minimumSize.height = minimumSize.height * documentScale;

    const interactingOptions = isCheckboxType
      ? {
          resizableOptions: {
            modifiers: [
              interact.modifiers.aspectRatio({
                ratio: 1,
                equalDelta: true,
                modifiers: [
                  interact.modifiers.restrictSize({
                    min: minimumSize,
                  }),
                  interact.modifiers.restrictEdges({
                    outer: relatedPage,
                  }),
                ],
              }),
            ],
          },
          draggableOptions: {
            allowFrom: fieldContainer.firstChild as HTMLElement,
          },
        }
      : {
          resizableOptions: {
            restrictSize: {
              min: minimumSize,
            },
          },
          draggableOptions: {
            allowFrom: fieldContainer.firstChild as HTMLElement,
          },
        };
    const listeners = interactable
      //@ts-ignore
      .interacting(interactingOptions)
      .on('dragstart', () => {
        setIsDragging(true);
        handleInteraction();
      })
      .on('dragend', () => {
        setIsDragging(false);
      })
      .on('dragmove', event => {
        Interact.dragMoveListener(event, documentScale);
        modifyDropDownMenu();
      })
      .on('resizestart', handleInteraction)
      .on('resizemove', event => {
        Interact.resizeMoveListener(event, documentScale);
        if (isFontResize && checkIfDateOrText(type)) {
          Interact.rearrangeFontSizeAsync(event.target as HTMLElement, {
            checkWidth:
              type === DocumentFieldTypes.Date || type === DocumentFieldTypes.Name,
            isAsync: type === DocumentFieldTypes.Text,
          });
        }
        modifyDropDownMenu();
      })
      .on('resizeend', event => {
        const { x, y } = Interact.getNodeCoords(event.target);
        const width = event.rect.width;
        const height = event.rect.height;

        const updatePayload = {
          coordinateX: x - relatedPage.offsetLeft,
          coordinateY: y - relatedPage.offsetTop,
          width: width / documentScale,
          height: height / documentScale,
          style: {
            left: x,
            top: y,
            width: width / documentScale,
            height: height / documentScale,
          },
        } as Required<DocumentField>;

        if (isFontResize && checkIfDateOrText(type)) {
          const fontSize = Interact.getNodeFontSize(event.target);
          updatePayload.fontSize = fontSize;
          updatePayload.style.fontSize = fontSize;
        }

        if (minimizeWidth && imgRef.current) {
          const rate = imgRef.current.naturalWidth / imgRef.current.naturalHeight;
          const minimizedWidth = (height / documentScale) * rate;

          if (minimizedWidth < updatePayload.width) {
            updatePayload.width = minimizedWidth;
            updatePayload.style.width = minimizedWidth;
          }
        }

        updateDocumentField(updatePayload);
      })
      .on('tap', event => {
        if (!isMenuOpen && event.currentTarget.id === id) {
          openMenu();
          handleInteraction();
        }
      })
      .on('doubletap', () => {
        if (isRequisiteType && isPreparer) {
          openRequisiteSelectModal();
        }
      });

    return listeners;
  }, [
    containerRef,
    documentScale,
    id,
    getRelatedPage,
    handleInteraction,
    isCheckboxType,
    isFontResize,
    isPreparer,
    isRequisiteType,
    modifyDropDownMenu,
    openMenu,
    isMenuOpen,
    openRequisiteSelectModal,
    type,
    updateDocumentField,
    minimizeWidth,
    imgRef,
  ]);

  const handleUpdateRequisisteField = useCallback(() => {
    if (!requisiteId && lastSelectedSignature) {
      updateDocumentField({
        requisiteId:
          type === DocumentFieldTypes.Signature
            ? lastSelectedSignature.id
            : lastSelectedSignature.siblingId,
      });
    } else {
      openRequisiteSelectModal();
    }
  }, [
    requisiteId,
    type,
    lastSelectedSignature,
    openRequisiteSelectModal,
    updateDocumentField,
  ]);

  const setSignInteracting = useCallback(() => {
    const fieldContainer = containerRef.current as HTMLElement;
    return new Interact(fieldContainer).interactable.on('click', () => {
      handleFocus();
      if (isRequisiteType) {
        handleUpdateRequisisteField();
      }
      if (isCheckboxType) {
        toggleFieldChecked();
      }
    });
  }, [
    containerRef,
    handleUpdateRequisisteField,
    isCheckboxType,
    isRequisiteType,
    handleFocus,
    toggleFieldChecked,
  ]);

  useEffect(() => {
    const fieldContainer = containerRef.current as HTMLElement;

    if (!fieldContainer || disabled) return;

    switch (interactMode) {
      case InteractModes.PREPARING: {
        const interactListeners = setPrepareInteracting();

        return () => interactListeners.unset();
      }
      case InteractModes.SIGNING: {
        const interactListeners = setSignInteracting();

        return () => interactListeners.unset();
      }
    }
  }, [containerRef, disabled, interactMode, setPrepareInteracting, setSignInteracting]);

  useEffect(() => {
    const fieldRef = textFieldRef.current || containerRef.current;

    if (fieldRef && inFocus && lastFocusedFieldId !== field.id) {
      setLastFocusedFieldId && setLastFocusedFieldId(field.id);

      fieldRef.focus();
    }
  }, [containerRef, field.id, inFocus, lastFocusedFieldId, setLastFocusedFieldId]);

  return (
    <FieldItemView
      id={id}
      isFilled={
        !!fieldHasValue(field, type) &&
        (interactMode === InteractModes.SIGNING || isPreparer)
      }
      inFocus={inFocus}
      fieldType={type}
      isMenuOpen={popupAvailable && isMenuOpen}
      ref={containerRef}
      style={viewStyles}
      fieldColor={fieldColor}
      className={classNames({
        'fieldDropDown--tapable': interactMode === InteractModes.SIGNING,
        'fieldDropDown--required': interactMode === InteractModes.SIGNING && required,
      })}
      renderFieldTrigger={() => (
        <FieldTrigger
          containerRef={containerRef}
          imgRef={imgRef}
          textFieldRef={textFieldRef}
          field={field}
          fieldColor={fieldColor}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onUpdateDocumentField={updateDocumentField}
          inFocus={inFocus}
          interactMode={interactMode}
          isDragging={isDragging}
          requisiteId={requisiteId}
          signer={signer}
          textStyles={textStyles}
          disabled={disabled}
          getRelatedPage={getRelatedPage}
        />
      )}
      renderFieldMenu={() => (
        <FieldItemMenu
          field={field}
          signer={signer}
          signersOptions={signersOptions}
          onChangeSigner={onChangeSigner}
          onUpdateDocumentField={updateDocumentField}
        />
      )}
      isResizable={isResizable && interactMode !== InteractModes.SIGNING}
      dropdownMenuRef={dropdownMenuRef}
      dropdownClassName={dropdownMenuClassName}
      selectedScale={0.9 * documentScale}
      fieldModalPosition={fieldModalPosition}
    />
  );
}

export default FieldItem;
